import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { ListPageShell, ListPageHeader, ListSearchBar } from '../components/ui/ListPageChrome';

export default function Vendors() {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalAll, setTotalAll] = useState(0);
  const [totalActive, setTotalActive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const [reviewModal, setReviewModal] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, review_text: '', reviewer: '' });
  const [savingReview, setSavingReview] = useState(false);
  const [reviewError, setReviewError] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const qs = new URLSearchParams({ page: String(page), limit: String(limit), paginate: '1' });
      if (search.trim()) qs.set('search', search.trim());
      const data = await api.get(`/vendors?${qs}`);
      if (Array.isArray(data)) {
        setVendors(data);
        setTotal(data.length);
        setTotalAll(data.length);
        setTotalActive(data.length);
      } else {
        setVendors(data.rows || []);
        setTotal(data.total ?? 0);
        setTotalAll(data.total_all ?? data.total ?? 0);
        setTotalActive(data.total_active ?? data.total_all ?? data.total ?? 0);
      }
    } catch {
      setVendors([]);
      setTotal(0);
      setTotalAll(0);
      setTotalActive(0);
    } finally {
      setLoading(false);
    }
  }, [search, page, limit]);

  useEffect(() => { setPage(1); }, [search]);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async (v) => {
    if (!window.confirm(`Delete vendor ${v.vendor_code} — ${v.vendor_name}?`)) return;
    setDeleting(v.vendor_id);
    try {
      await api.del(`/vendors/${v.vendor_id}`);
      await load();
    } catch (e) {
      alert(e.message || 'Cannot delete vendor');
    } finally {
      setDeleting(null);
    }
  };

  const openReviews = async (v) => {
    setReviewModal(v);
    setReviewError('');
    setReviewForm({ rating: 5, review_text: '', reviewer: '' });
    setLoadingReviews(true);
    try {
      const data = await api.get(`/vendors/${v.vendor_id}/reviews`);
      setReviews(Array.isArray(data) ? data : []);
    } catch {
      setReviews([]);
    } finally {
      setLoadingReviews(false);
    }
  };

  const handleAddReview = async () => {
    if (!reviewForm.reviewer.trim()) { setReviewError('Reviewer name is required'); return; }
    setSavingReview(true);
    setReviewError('');
    try {
      await api.post(`/vendors/${reviewModal.vendor_id}/reviews`, reviewForm);
      const data = await api.get(`/vendors/${reviewModal.vendor_id}/reviews`);
      setReviews(Array.isArray(data) ? data : []);
      setReviewForm({ rating: 5, review_text: '', reviewer: '' });
      await load();
    } catch (e) {
      setReviewError(e.message || 'Failed to add review');
    } finally {
      setSavingReview(false);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    try {
      await api.del(`/vendors/reviews/${reviewId}`);
      setReviews((prev) => prev.filter((r) => r.review_id !== reviewId));
      await load();
    } catch { /* ignore */ }
  };

  const renderStars = (rating, size = 12) => (
    Array.from({ length: 5 }, (_, i) => (
      <svg key={i} width={size} height={size} viewBox="0 0 24 24"
        fill={i < rating ? '#D4A017' : '#E0E0E0'} stroke="none">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ))
  );

  const totalPages = Math.max(1, Math.ceil(total / limit) || 1);

  return (
    <ListPageShell>
      <ListPageHeader
        title="Vendors"
        subtitle={
          loading
            ? 'Loading…'
            : search.trim()
              ? `${total} matching vendor${total !== 1 ? 's' : ''}`
              : `${totalActive || total} active vendor${(totalActive || total) !== 1 ? 's' : ''}${totalAll > 0 && totalAll !== totalActive ? ` · ${totalAll} total` : ''}`
        }
        action={
          <button type="button" onClick={() => navigate('/vendor/new')} className="btn-primary">
            + Add Vendor
          </button>
        }
      />

      <ListSearchBar
        value={search}
        onChange={setSearch}
        placeholder="Search by code, name, or contact…"
      />

      <div className="data-table-wrap overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="table-head">
              {['Code', 'Vendor Name', 'Contact Person', 'Payment Terms', 'Currency', 'Rating', 'POs', ''].map((h) => (
                <th key={h || 'actions'} className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-sap-textLight">Loading…</td></tr>
            ) : vendors.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-sap-textLight">No vendors found.</td></tr>
            ) : vendors.map((v) => (
              <tr
                key={v.vendor_id}
                onClick={() => navigate(`/vendor/${v.vendor_id}`)}
                className="table-row even:bg-gray-50/50 cursor-pointer"
              >
                <td className="px-4 py-3 font-semibold text-sap-blue">{v.vendor_code}</td>
                <td className="px-4 py-3 font-medium">{v.vendor_name}</td>
                <td className="px-4 py-3 text-sap-textLight">{v.contact_person || '—'}</td>
                <td className="px-4 py-3 text-sap-textLight">{v.payment_terms || '—'}</td>
                <td className="px-4 py-3">{v.currency || 'INR'}</td>
                <td className="px-4 py-3">
                  {v.avg_rating ? (
                    <div className="flex items-center gap-1">
                      <span className="flex">{renderStars(Math.round(Number(v.avg_rating)))}</span>
                      <span className="text-xs text-sap-textLight">
                        {Number(v.avg_rating).toFixed(1)} ({v.review_count || 0})
                      </span>
                    </div>
                  ) : (
                    <span className="text-sap-textLight">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-center">{v.po_count || 0}</td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => openReviews(v)}
                      className="btn-secondary text-xs py-1 px-2"
                    >
                      Reviews
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(v)}
                      disabled={deleting === v.vendor_id}
                      className="px-2 py-1 text-xs font-medium text-red-600 border border-red-200 rounded-xl hover:bg-red-50 disabled:opacity-50 transition-colors"
                    >
                      {deleting === v.vendor_id ? '…' : 'Delete'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-4 py-3 border-t border-sap-border/60 flex flex-wrap items-center justify-between gap-3 text-xs text-sap-textLight bg-[#F7F5F2]">
          <span>
            {total === 0 && !loading
              ? 'No records'
              : `Showing ${total === 0 ? 0 : (page - 1) * limit + 1}–${Math.min(page * limit, total)} of ${total}`}
          </span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => p - 1)}
              className="px-2 py-1 border border-sap-border rounded bg-white disabled:opacity-40 hover:bg-white"
            >
              ‹
            </button>
            <span className="text-sap-text">Page {page} of {totalPages}</span>
            <button
              type="button"
              disabled={page >= totalPages || total === 0 || loading}
              onClick={() => setPage((p) => p + 1)}
              className="px-2 py-1 border border-sap-border rounded bg-white disabled:opacity-40 hover:bg-white"
            >
              ›
            </button>
          </div>
          <label className="flex items-center gap-2">
            Rows per page
            <select
              value={limit}
              onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
              className="border border-sap-border rounded px-2 py-1 bg-white text-sap-text"
            >
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {reviewModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setReviewModal(false)}
        >
          <div className="card max-w-lg w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="card-header justify-between">
              <span className="text-sm font-semibold text-sap-text">
                Reviews — {reviewModal.vendor_name}
              </span>
              <button type="button" onClick={() => setReviewModal(false)} className="text-sap-textMuted hover:text-sap-text text-lg leading-none">✕</button>
            </div>

            <div className="p-4 space-y-3 overflow-y-auto flex-1">
              {loadingReviews ? (
                <p className="text-sm text-sap-textLight text-center py-4">Loading…</p>
              ) : reviews.length === 0 ? (
                <p className="text-sm text-sap-textLight text-center py-4">No reviews yet.</p>
              ) : reviews.map((r) => (
                <div key={r.review_id} className="border-b border-sap-border pb-3 last:border-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex">{renderStars(r.rating, 14)}</span>
                    <button
                      type="button"
                      onClick={() => handleDeleteReview(r.review_id)}
                      className="text-xs text-red-500 hover:underline shrink-0"
                    >
                      Delete
                    </button>
                  </div>
                  {r.review_text && <p className="text-sm text-sap-text mt-1">{r.review_text}</p>}
                  <p className="text-xs text-sap-textMuted mt-1">
                    {r.reviewer}
                    {r.created_at ? ` · ${new Date(r.created_at).toLocaleDateString()}` : ''}
                  </p>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-sap-border space-y-3 shrink-0">
              <div>
                <label className="text-xs font-semibold text-sap-text">Rating</label>
                <select
                  value={reviewForm.rating}
                  onChange={(e) => setReviewForm((f) => ({ ...f, rating: Number(e.target.value) }))}
                  className="w-full border border-sap-border rounded-xl px-3 py-2 text-sm mt-1 bg-white focus:outline-none focus:ring-2 focus:ring-sap-blue/20 focus:border-sap-blue"
                >
                  {[5, 4, 3, 2, 1].map((n) => (
                    <option key={n} value={n}>{n} star{n !== 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-sap-text">Reviewer</label>
                <input
                  value={reviewForm.reviewer}
                  onChange={(e) => setReviewForm((f) => ({ ...f, reviewer: e.target.value }))}
                  className="search-input mt-1"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-sap-text">Review</label>
                <textarea
                  value={reviewForm.review_text}
                  onChange={(e) => setReviewForm((f) => ({ ...f, review_text: e.target.value }))}
                  className="search-input mt-1 min-h-[72px] resize-y"
                  placeholder="Write your review…"
                />
              </div>
              {reviewError && <p className="text-xs text-red-600">{reviewError}</p>}
              <button type="button" onClick={handleAddReview} disabled={savingReview} className="btn-primary w-full">
                {savingReview ? 'Saving…' : 'Add Review'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ListPageShell>
  );
}
